import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Sidebar.css';
import { useData } from '../context/DataContext';
import { useTouchFriendlyUI } from '../utils/useTouchFriendlyUI';
import { formatCatalogDisplayName } from '../utils/categoryMenuTree';
import DualRangeSlider from './DualRangeSlider';
import { CategoryIcon } from '../utils/sidebarCategoryIcons';
import { filterBrowsableProducts } from '../utils/catalogProductShape';
import { productMatchesSelectedCategory } from '../utils/catalogIds';
import {
  filterProductsForPriceSlider,
  getCatalogMaxPrice,
  getCatalogPriceStep,
  snapPriceToStep
} from '../utils/filterPrice';

function slugify(str) {
  return String(str || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

function FilterAccordion({ id, title, open, onToggle, children }) {
  return (
    <div className={`sidebar-accordion${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="sidebar-accordion__trigger"
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
      >
        <span>{title}</span>
        <span className="sidebar-accordion__chevron" aria-hidden="true">
          {open ? '▲' : '▼'}
        </span>
      </button>
      {open ? (
        <div id={id} className="sidebar-accordion__panel">
          {children}
        </div>
      ) : null}
    </div>
  );
}

const Sidebar = ({ filters, onFilterChange }) => {
  const { categories, subcategoriesByCategory, products } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const useTapMenus = useTouchFriendlyUI(968);
  const sidebarCategoriesRef = useRef(null);
  const [openPrice, setOpenPrice] = useState(true);
  const [openBrands, setOpenBrands] = useState(false);
  const [openStore, setOpenStore] = useState(false);
  const [openSubcategory, setOpenSubcategory] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const hoverCloseTimerRef = useRef(null);

  useEffect(() => {
    setHoveredCategory(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!hoveredCategory) return undefined;

    const closeIfOutside = (event) => {
      if (sidebarCategoriesRef.current?.contains(event.target)) return;
      setHoveredCategory(null);
    };

    document.addEventListener('mousedown', closeIfOutside);
    document.addEventListener('touchstart', closeIfOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', closeIfOutside);
      document.removeEventListener('touchstart', closeIfOutside);
    };
  }, [hoveredCategory]);

  const scheduleHoverClose = () => {
    if (useTapMenus) return;
    if (hoverCloseTimerRef.current) clearTimeout(hoverCloseTimerRef.current);
    hoverCloseTimerRef.current = setTimeout(() => {
      setHoveredCategory(null);
      hoverCloseTimerRef.current = null;
    }, 140);
  };

  const cancelHoverClose = () => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  };

  useEffect(
    () => () => {
      if (hoverCloseTimerRef.current) clearTimeout(hoverCloseTimerRef.current);
    },
    []
  );

  const handleSubcategoryNavigate = (category, sub) => {
    const catSlug = category.slug || slugify(category.name);
    const subSlug = sub.slug || slugify(sub.name);
    navigate(`/category/${catSlug}/${subSlug}`);
    setHoveredCategory(null);
  };

  const handleCategoryNavigate = (category) => {
    if (category.id === 'all') {
      selectCategory('all');
      return;
    }
    const catSlug = category.slug || slugify(category.name);
    navigate(`/category/${catSlug}`);
    setHoveredCategory(null);
  };

  const productsForPrice = useMemo(
    () => filterProductsForPriceSlider(products, filters, categories),
    [products, filters, categories]
  );

  const productsForOptions = useMemo(() => {
    let list = filterBrowsableProducts(products);
    if (filters.category && filters.category !== 'all') {
      list = list.filter((p) => productMatchesSelectedCategory(p, filters.category, categories));
    }
    return list;
  }, [products, filters.category, categories]);

  const catalogMaxPrice = useMemo(
    () => getCatalogMaxPrice(productsForPrice),
    [productsForPrice]
  );
  const priceStep = useMemo(() => getCatalogPriceStep(catalogMaxPrice), [catalogMaxPrice]);
  const priceMin = Math.min(filters.priceMin ?? 0, catalogMaxPrice);
  const effectiveMax = filters.priceMax == null ? catalogMaxPrice : Math.min(filters.priceMax, catalogMaxPrice);
  const atFullRange = filters.priceMax == null || effectiveMax >= catalogMaxPrice;

  const allCategories = useMemo(
    () => [
      { id: 'all', name: 'All Categories', slug: 'all' },
      ...(categories || []).map((cat) => ({
        id: cat._id || cat.id,
        name: cat.name,
        slug: cat.slug
      }))
    ],
    [categories]
  );

  const subcategoryOptions = useMemo(() => {
    const acc = [];
    const seen = new Set();
    const pushSub = (sub) => {
      if (!sub) return;
      const id = String(sub.id || sub._id || '').trim();
      const rid = String(sub.railwayId || '').trim();
      const name = String(sub.name || '').trim();
      if (!name && !id && !rid) return;
      const value = [id, rid].filter(Boolean).join('::') || name;
      const key = `${value}::${name.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      acc.push({ value, name });
    };
    if (filters.category && filters.category !== 'all') {
      (subcategoriesByCategory?.[filters.category] || []).forEach(pushSub);
    } else {
      Object.values(subcategoriesByCategory || {}).forEach((list) => (list || []).forEach(pushSub));
    }
    acc.sort((a, b) => a.name.localeCompare(b.name));
    return acc;
  }, [filters.category, subcategoriesByCategory]);

  const brandOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    productsForOptions.forEach((p) => {
      const b = String(p.brand || '').trim();
      if (!b) return;
      const key = b.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(b);
    });
    return out.sort((a, b) => a.localeCompare(b));
  }, [productsForOptions]);

  const storeOptions = useMemo(() => {
    const out = [];
    const seen = new Set();
    productsForOptions.forEach((p) => {
      const id = String(p.storeId || p.store?._id || p.store?.id || '').trim();
      const name = String(p.storeName || p.store?.name || p.vendor || '').trim();
      if (!id || !name) return;
      if (seen.has(id)) return;
      seen.add(id);
      out.push({ id, name });
    });
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [productsForOptions]);

  const formatKsh = (n) =>
    `Ksh ${Number(n).toLocaleString('en-KE', { maximumFractionDigits: 0 })}`;

  const formatMaxLabel = () => (atFullRange ? `${formatKsh(catalogMaxPrice)}+` : formatKsh(effectiveMax));

  const handlePriceChange = (minVal, maxVal) => {
    const min = snapPriceToStep(minVal, priceStep);
    const max = snapPriceToStep(maxVal, priceStep);
    const isFull = min <= 0 && max >= catalogMaxPrice;
    onFilterChange({
      priceMin: min,
      priceMax: isFull ? null : max,
      priceRange: 'all'
    });
  };

  const selectCategory = (categoryId) => {
    onFilterChange({
      category: categoryId,
      subCategory: 'all',
      subCategoryName: '',
      brand: 'all'
    });
  };

  const selectBrand = (brand) => {
    onFilterChange({ brand: brand || 'all' });
  };

  const selectStore = (storeId) => {
    onFilterChange({ store: storeId || 'all' });
  };

  return (
    <aside className="sidebar sidebar--modern">
      <section className="sidebar-block sidebar-block--categories" ref={sidebarCategoriesRef}>
        <h3 className="sidebar-heading">Category</h3>
        <ul className="sidebar-categories" role="list">
          {allCategories.map((category) => {
            const selected = filters.category === category.id;
            const isAll = category.id === 'all';
            const catId = String(category.id);
            const subs = isAll ? [] : subcategoriesByCategory?.[catId] || [];
            const hasSubs = subs.length > 0;
            const flyoutOpen = hoveredCategory === catId && hasSubs;

            const openFlyout = () => {
              cancelHoverClose();
              setHoveredCategory(catId);
            };

            const toggleFlyout = () => {
              setHoveredCategory(flyoutOpen ? null : catId);
            };

            const handleMainCategoryClick = () => {
              if (isAll) {
                selectCategory('all');
                setHoveredCategory(null);
                return;
              }
              if (useTapMenus && hasSubs) {
                handleCategoryNavigate(category);
                return;
              }
              selectCategory(category.id);
              setHoveredCategory(null);
            };

            return (
              <li
                key={category.id}
                className={`sidebar-category-item${flyoutOpen ? ' sidebar-category-item--open' : ''}${hasSubs ? ' sidebar-category-item--has-subs' : ''}`}
                onMouseEnter={() => {
                  if (useTapMenus || isAll || !hasSubs) return;
                  openFlyout();
                }}
                onMouseLeave={() => {
                  if (useTapMenus || isAll || !hasSubs) return;
                  scheduleHoverClose();
                }}
              >
                <div className="sidebar-category-btn-row">
                  <button
                    type="button"
                    className={`sidebar-category-btn sidebar-category-btn--main${selected ? ' is-active' : ''}`}
                    onClick={handleMainCategoryClick}
                    aria-current={selected ? 'true' : undefined}
                  >
                    <span className="sidebar-category-btn__icon">
                      <CategoryIcon category={category} />
                    </span>
                    <span className="sidebar-category-btn__label">{formatCatalogDisplayName(category.name)}</span>
                  </button>
                  {hasSubs ? (
                    <button
                      type="button"
                      className="sidebar-category-btn__toggle"
                      aria-label={`${flyoutOpen ? 'Hide' : 'Show'} ${category.name} subcategories`}
                      aria-expanded={flyoutOpen}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFlyout();
                      }}
                    >
                      <span className="sidebar-category-btn__chevron" aria-hidden="true">
                        ›
                      </span>
                    </button>
                  ) : null}
                </div>
                {flyoutOpen ? (
                  <div
                    className="sidebar-category-flyout"
                    onMouseEnter={useTapMenus ? undefined : cancelHoverClose}
                    onMouseLeave={useTapMenus ? undefined : scheduleHoverClose}
                  >
                    <div className="sidebar-category-flyout-inner">
                      {subs.map((sub) => (
                        <button
                          key={sub.id || sub._id || sub.name}
                          type="button"
                          className="sidebar-subcategory-item"
                          title={sub.name}
                          onClick={() => handleSubcategoryNavigate(category, sub)}
                        >
                          {formatCatalogDisplayName(sub.name)}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>

      <hr className="sidebar-divider" />

      <section className="sidebar-block">
        <h3 className="sidebar-heading">Filter by</h3>

        <FilterAccordion
          id="sidebar-filter-price"
          title="Price Range"
          open={openPrice}
          onToggle={() => setOpenPrice((v) => !v)}
        >
          <DualRangeSlider
            min={0}
            max={catalogMaxPrice}
            step={priceStep}
            valueMin={priceMin}
            valueMax={effectiveMax}
            onChange={handlePriceChange}
          />
          <div className="sidebar-price-labels">
            <span>{formatKsh(priceMin)}</span>
            <span>{formatMaxLabel()}</span>
          </div>
          {(filters.priceMin > 0 || filters.priceMax != null) && (
            <button
              type="button"
              className="sidebar-filter-reset"
              onClick={() =>
                onFilterChange({ priceMin: 0, priceMax: null, priceRange: 'all' })
              }
            >
              Reset price
            </button>
          )}
        </FilterAccordion>

        {brandOptions.length > 0 ? (
          <FilterAccordion
            id="sidebar-filter-brands"
            title="Brands"
            open={openBrands}
            onToggle={() => setOpenBrands((v) => !v)}
          >
            <ul className="sidebar-check-list" role="list">
              <li>
                <label className="sidebar-check-item">
                  <input
                    type="radio"
                    name="sidebar-brand"
                    checked={!filters.brand || filters.brand === 'all'}
                    onChange={() => selectBrand('all')}
                  />
                  <span>All brands</span>
                </label>
              </li>
              {brandOptions.map((brand) => (
                <li key={brand}>
                  <label className="sidebar-check-item">
                    <input
                      type="radio"
                      name="sidebar-brand"
                      checked={filters.brand === brand}
                      onChange={() => selectBrand(brand)}
                    />
                    <span>{brand}</span>
                  </label>
                </li>
              ))}
            </ul>
          </FilterAccordion>
        ) : null}

        {storeOptions.length > 0 ? (
          <FilterAccordion
            id="sidebar-filter-store"
            title="Store"
            open={openStore}
            onToggle={() => setOpenStore((v) => !v)}
          >
            <ul className="sidebar-check-list" role="list">
              <li>
                <label className="sidebar-check-item">
                  <input
                    type="radio"
                    name="sidebar-store"
                    checked={!filters.store || filters.store === 'all'}
                    onChange={() => selectStore('all')}
                  />
                  <span>All stores</span>
                </label>
              </li>
              {storeOptions.map((s) => (
                <li key={s.id}>
                  <label className="sidebar-check-item">
                    <input
                      type="radio"
                      name="sidebar-store"
                      checked={filters.store === s.id}
                      onChange={() => selectStore(s.id)}
                    />
                    <span>{s.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          </FilterAccordion>
        ) : null}

        {subcategoryOptions.length > 0 ? (
          <FilterAccordion
            id="sidebar-filter-subcategory"
            title="Subcategory"
            open={openSubcategory}
            onToggle={() => setOpenSubcategory((v) => !v)}
          >
            <ul className="sidebar-check-list" role="list">
              <li>
                <label className="sidebar-check-item">
                  <input
                    type="radio"
                    name="sidebar-subcategory"
                    checked={!filters.subCategory || filters.subCategory === 'all'}
                    onChange={() =>
                      onFilterChange({ subCategory: 'all', subCategoryName: '' })
                    }
                  />
                  <span>All subcategories</span>
                </label>
              </li>
              {subcategoryOptions.map((sub) => (
                <li key={sub.value}>
                  <label className="sidebar-check-item">
                    <input
                      type="radio"
                      name="sidebar-subcategory"
                      checked={filters.subCategory === sub.value}
                      onChange={() =>
                        onFilterChange({
                          subCategory: sub.value,
                          subCategoryName: sub.name
                        })
                      }
                    />
                    <span>{formatCatalogDisplayName(sub.name)}</span>
                  </label>
                </li>
              ))}
            </ul>
          </FilterAccordion>
        ) : null}
      </section>
    </aside>
  );
};

export default Sidebar;
